import type { Theme } from '@mui/material/styles'

/**
 * Additions to `getEnhancedTableStyles().table` for the widths where a table
 * cannot show every column. Spread after it: `{ ...tableStyles.table, ...these }`.
 */

/**
 * Tablet widths (768–1199), for a table that keeps its priority columns and
 * moves the rest into a RowExpander.
 *
 * Column widths come from the header cells rather than from the content.
 * Sized by content, every column claims its widest unbreakable piece — a
 * chip, a header label plus its sort arrow — and on an 834px screen, which
 * leaves 524px beside the 260px rail, those alone outgrow the container and
 * the table scrolls sideways. Fixed widths cannot outgrow it: text wraps and
 * chips truncate inside them. Give every column but one a width; the one
 * without takes what the others leave.
 */
export const getCompactTableSx = (theme: Theme) => ({
  tableLayout: 'fixed' as const,
  '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root': {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '& .MuiTableBody-root .MuiTableCell-root': { overflowWrap: 'anywhere' as const },
  // A chip that sets its own max-width (the warm-contacts chip caps at 160px)
  // would otherwise overflow a column narrower than that cap.
  '& .MuiTableBody-root tr:not([data-expander]) .MuiChip-root': { maxWidth: '100%' },
  // Lets the arrow drop under a label its column is too narrow to share a line with.
  '& .MuiTableSortLabel-root': { flexWrap: 'wrap' as const },
  '& .MuiTableSortLabel-icon': { marginLeft: '2px', marginRight: 0 },
  // An unsorted column's arrow only ever shows on mouse hover, yet it reserves
  // its width in every header. Under 900 that width is worth more to the
  // content; the sorted column keeps its arrow.
  [theme.breakpoints.down('md')]: {
    '& .MuiTableSortLabel-root:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' },
  },
  // The expander row is styled from here because a cell's own sx loses to
  // tableStyles' body-cell padding, which leaves a 26px blank band under every
  // row while the expander is shut.
  '& .MuiTableBody-root [data-expander]': {
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  },
  '& .MuiTableBody-root [data-expander] > .MuiTableCell-root': { paddingTop: 0, paddingBottom: 0 },
  '& .MuiTableBody-root [data-expander="closed"] > .MuiTableCell-root': { borderBottom: 'none' },
})

/**
 * 1200–1279, where a table shows its full column set: the narrowest desktop
 * widths cannot fit the widest column set at the standard 14px cell padding,
 * so the padding tightens to the tablet value there and nowhere else.
 */
export const getTightDesktopTableSx = (theme: Theme) => ({
  [theme.breakpoints.between(1200, 1280)]: {
    '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
})
